import { FC, useState } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import ProductsSlider, {
  ProductSlide,
  SliderDirection
} from '@/components/organisms/productsSlider'
import InnerImageZoom from 'react-inner-image-zoom'
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css'
import { BreakpointKey, getMedia } from '@/theme/breakpoints'
import styled from 'styled-components'
import {
  ElementWithPlaceholder,
  PictureWithPlaceholder
} from '@/components/atoms/picture'
import Head from 'next/head'
import { getProductImageUrls } from '@/utils/productImage'
import { useProductImage } from 'hooks/useProductImage'

const mainImageSize = { width: 1400, height: 1400 }

type ThumbnailProps = {
  url: string
  useThumbor: boolean
  onClick: () => void
  onMouseEnter: () => void
}

const Thumbnail: FC<ThumbnailProps> = ({
  url,
  useThumbor,
  onClick,
  onMouseEnter
}) => {
  const image = useProductImage(
    getProductImageUrls(
      url,
      { width: 400, height: 400 },
      { fallbackSize: { width: 800, height: 800 }, useThumbor }
    ),
    { area: 'pdp-desktop-thumb', measureSlow: false }
  )
  return (
    <PictureWithPlaceholder
      src={image.src}
      preloadDefaultMedia={BreakpointKey.desktop}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      ratio={1}
      onImageLoad={image.onLoad}
      onImageError={image.onError}
      imageRef={image.ref}
    />
  )
}

type ImageBrowserDesktopProps = {
  initialIndex: number
  imageUrls: string[]
  slidesToShow?: number
  onImageClick: (offset: number) => void
  hoverColorVariant: string
  productLabels?: JSX.Element
  useThumbor: boolean
}

const ImageBrowserDesktop: FC<ImageBrowserDesktopProps> = ({
  imageUrls,
  onImageClick,
  slidesToShow = 4,
  initialIndex,
  hoverColorVariant,
  productLabels,
  useThumbor
}) => {
  const [imageIndex, setImageIndex] = useState<number>(initialIndex)

  const mainImage = useProductImage(
    getProductImageUrls(
      hoverColorVariant ? hoverColorVariant : imageUrls[imageIndex],
      mainImageSize,
      { useThumbor }
    ),
    { area: 'pdp-desktop-main', measureSlow: true }
  )

  return (
    <>
      <Flex
        position="relative"
        display={{ desktop: 'flex', tablet: 'none', mobile: 'none' }}
      >
        <Box width="18%" data-testid="imageBrowserDesktopThumbnails">
          <ProductsSlider
            spaceBetween={9}
            sliderDirection={SliderDirection.vertical}
            slidesToShow={slidesToShow}
            slideHeightPercent={100 / slidesToShow}
          >
            {imageUrls &&
              imageUrls.map((url, offset) => (
                <ProductSlide key={offset}>
                  <Thumbnail
                    url={url}
                    useThumbor={useThumbor}
                    onClick={() => onImageClick(imageIndex)}
                    onMouseEnter={() => setImageIndex(offset)}
                  />
                </ProductSlide>
              ))}
          </ProductsSlider>
        </Box>
        <Box
          width="82%"
          onClick={() => onImageClick(imageIndex)}
          data-testid="imageBrowserDesktop"
          pl={24}
          position="relative"
        >
          <Head>
            <link
              rel="preload"
              href={
                getProductImageUrls(imageUrls[initialIndex], mainImageSize, {
                  useThumbor
                }).primary
              }
              as="image"
              media={getMedia(BreakpointKey.desktop)}
              {...{ fetchpriority: 'high' }}
            />
          </Head>
          <ElementWithPlaceholder ratio={1} elementClass="product-detail-image">
            <StyledWrapper>
              <InnerImageZoom
                // this will load transparent gif pixel for tablet/mobile
                src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
                sources={[
                  {
                    srcSet: mainImage.src,
                    media: getMedia(BreakpointKey.desktop)
                  }
                ]}
                zoomSrc={mainImage.src}
                zoomType="hover"
                hideHint={true}
                imgAttributes={{
                  className: 'product-detail-image',
                  onLoad: mainImage.onLoad,
                  onError: mainImage.onError,
                  ...{ fetchpriority: 'high', ref: mainImage.ref }
                }}
              />
            </StyledWrapper>
          </ElementWithPlaceholder>
          {productLabels && productLabels}
        </Box>
      </Flex>
    </>
  )
}

export default ImageBrowserDesktop

const StyledWrapper = styled(Box)`
  & figure {
    display: block;
  }
`
